import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectHitlExpressTimerMaxSeconds,
} from '../../redux/features/config/config.selector';
import { selectTaskAssignedSessionStartedAt } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectFinalOrderSubmitted } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectIsCarPresent } from '../../selectors/message';
import PulseMeterTile from './PulseMeterTile';

/**
 * MENU BOARD TIME (HITL 3.0 Pulse tile).
 *
 * Time since the session started, against the express-mode threshold. The
 * threshold comes from the Unleash `hitl_express_timer` `max_seconds` variant
 * (`selectHitlExpressTimerMaxSeconds`), NOT an env var.
 *
 * ALWAYS rendered while hitl_3_0 is on (it never disappears): it is the fixed
 * "how long has this car been here" gauge in the Pulse card.
 *
 *   • Idle / before any car   → 0:00, green, empty bars (default).
 *   • Car present             → counts up every second from the session start.
 *   • Car exits               → FREEZES at the elapsed value and holds it.
 *   • Finish/Cancel Order      → while the car is gone, the interaction is over,
 *     (car already gone)         so it resets to the 0:00 default. (While the car
 *                                is still present it keeps counting — no reset.)
 *   • Next car enters         → resets to 0:00 and counts again.
 *
 * The session-start anchor mirrors the header timer it replaces: the
 * task_assigned `session_started_at` when present (for a task-router job this is
 * effectively the car_enter moment), otherwise the time the car became present.
 * `isCarPresent` (car_enter → true, car_exit → false; untouched by session-end /
 * Cancel / Finish bookkeeping) is what starts, freezes and resets the clock.
 *
 * It does NOT blink over the limit — it escalates green -> amber -> red and caps
 * at red. M:SS, updated every second. Gated entirely by hitl_3_0.
 */

// Fixed segment count (matches the Response time tile), independent of the
// threshold — one-bar-per-second would be unusable for a minutes-long limit.
const MENU_BOARD_BAR_COUNT = 6;

// Colour-band threshold used when the Unleash `max_seconds` variant isn't set,
// so the tile still escalates on every order.
const DEFAULT_MENU_BOARD_LIMIT_SECONDS = 120;

// M:SS, e.g. "0:07", "1:42".
const formatMinutesSeconds = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

const MenuBoardTimeTile = () => {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const maxSeconds = useAppSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useAppSelector(selectTaskAssignedSessionStartedAt);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const finalOrderSubmitted = useAppSelector(selectFinalOrderSubmitted);

  const limitSeconds =
    maxSeconds > 0 ? maxSeconds : DEFAULT_MENU_BOARD_LIMIT_SECONDS;

  const [elapsedMs, setElapsedMs] = useState(0);
  // The anchor we're currently counting from; non-null only while ticking. Used
  // to freeze at the exact elapsed value the instant the car leaves.
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isHitl3 || !isCarPresent) {
      // Car gone (or tile off): FREEZE. If we were ticking, snap to the exact
      // elapsed at this instant and stop; the value then holds until a car
      // returns. The ref guard keeps Cancel/Finish (and any other post-exit
      // state change) from re-touching the frozen value.
      if (startTimeRef.current != null) {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
        startTimeRef.current = null;
      }
      return undefined;
    }

    // Car present → (re)anchor and tick every second. Anchor to the
    // task_assigned session start when valid; otherwise from now (car-present
    // moment). A fresh anchor here is what resets the clock for the next car.
    const parsedStart = new Date(sessionStartedAt).getTime();
    const startTime =
      !Number.isNaN(parsedStart) && sessionStartedAt ? parsedStart : Date.now();
    startTimeRef.current = startTime;

    const tick = () => setElapsedMs(Math.max(0, Date.now() - startTime));
    tick(); // compute immediately so a past anchor is right on first paint
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isHitl3, isCarPresent, sessionStartedAt]);

  // Finish/Cancel Order while the car has already left ends the interaction, so
  // drop the frozen value back to the 0:00 default (ready for the next car).
  // `finalOrderSubmitted` pulses true on any finalize/cancel; we act on that edge
  // only when the car is gone — while it's still present the clock keeps running.
  useEffect(() => {
    if (finalOrderSubmitted && !isCarPresent) {
      startTimeRef.current = null;
      setElapsedMs(0);
    }
  }, [finalOrderSubmitted, isCarPresent]);

  if (!isHitl3) {
    return null;
  }

  return (
    <PulseMeterTile
      label="Menu Board Time"
      elapsedMs={elapsedMs}
      limitMs={limitSeconds * 1000}
      barCount={MENU_BOARD_BAR_COUNT}
      formatValue={formatMinutesSeconds}
      testId="menu-board-time-tile"
    />
  );
};

export default MenuBoardTimeTile;
